<template>
  <div class="trade-content">
    <NavigationPanelWrapper>
      <NavigationPanel>
        <div slot-scope="{ setPanel }">
          <GeneralSidebarList
            :balances="balances"
            :trade-status="tradeStatus"
            :on-access-home-broker-click="onAccessHomeBrokerClick"
            :set-panel="setPanel"
            :get-watchlist-subscription="initiateWatchListSubscription"
          />
        </div>
      </NavigationPanel>
      <NavigationPanel>
        <div slot-scope="{ setPanel }">
          <div class="sidebar-heading_container">
            <BackButton
              class="sidebar-heading_container__backbutton"
              @click="setPanel(sidebarTypes.General)"
            />
            <p class="text-p6-semibold">Todos meus favoritos</p>
          </div>
          <WatchListInfiniteScroll
            :go-back="() => setPanel(sidebarTypes.General)"
            :set-watchlist-subscription="initiateWatchListSubscription"
          />
        </div>
      </NavigationPanel>
    </NavigationPanelWrapper>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  NavigationPanelWrapper,
  NavigationPanel
} from '@/modules/common/components/NavigationPanel';
import { WatchListInfiniteScroll } from './components/WatchListInfiniteScroll';
import { GeneralSidebarList } from './components/GeneralSidebarList';
import { SidebarTypes } from './enums';

import {
  TradePosition,
  TradeInstrumentPositionPrice
} from '@/modules/trade/types/instrument';
import { Instrument as InstrumentWithWatchlist } from '@/modules/trade/services/search';

import { SignalR, MarketData } from '@/modules/trade/services/signalr';
import { AccountStatus, IHeroBalances } from '@/modules/trade/services/account';

const customerModule = namespace('CustomerModule');
const tradePositionsModule = namespace('tradeModule');

@Component({
  components: {
    BackButton,
    NavigationPanel,
    NavigationPanelWrapper,
    WatchListInfiniteScroll,
    GeneralSidebarList
  }
})
export default class TradeSidebar extends Vue {
  @Prop({ type: Object, required: false })
  readonly balances?: IHeroBalances;

  @Prop({ type: Object, required: false })
  readonly tradeStatus?: AccountStatus;

  @Prop({ type: Function, default: () => {} })
  readonly onAccessHomeBrokerClick!: Function;

  @tradePositionsModule.Getter('getPositions')
  readonly positions!: TradePosition[];

  @tradePositionsModule.Getter('getWatchlist')
  readonly watchlist!: InstrumentWithWatchlist[];

  @tradePositionsModule.Mutation('updateWatchlist')
  readonly updateWatchlist!: (data: MarketData) => void;

  @tradePositionsModule.Mutation('updatePosition')
  readonly updatePosition!: (data: TradeInstrumentPositionPrice) => void;

  @customerModule.Getter('getCustomerId') readonly customerId!: string;

  private sidebarTypes = SidebarTypes;
  private isWatchlistInvoked = false;

  created() {
    this.startSignalR();
  }

  destroyed() {
    this.stopSignalR();
  }

  private async startSignalR() {
    await SignalR.startConnection();

    SignalR.connection.on('MarketData', (data: MarketData) => {
      this.updateWatchlist(data);
    });

    SignalR.connection.onreconnected(async () => {
      this.isWatchlistInvoked = false;

      await this.initiateWatchListSubscription();
    });
  }

  private async stopSignalR() {
    await SignalR.stopConnection();
  }

  async initiateWatchListSubscription() {
    if (Array.isArray(this.watchlist) && this.watchlist.length !== 0) {
      if (!this.isWatchlistInvoked) {
        const watchlistSymbols = this.watchlist.map(
          watchlist => watchlist.symbol
        );
        await SignalR.invokeWithSubscription('JoinGroup', watchlistSymbols);
        this.isWatchlistInvoked = true;
      }
    }
  }
}
</script>

<style lang="less">
@time: 600ms;
@ease: cubic-bezier(0.55, 0, 0.1, 1);

.row-item {
  &.row-item__interaction {
    &:hover {
      background: @background-hover;
      cursor: pointer;
    }
    &:active {
      opacity: 0.2;
    }
  }
}
.trade-content {
  height: 100%;
  section {
    overflow: initial;
    z-index: auto;
  }
}
.trade-search_input {
  flex-basis: 90%;
}

.trade-label {
  height: @size-spacing-x600;
  color: @background-overlay;
  margin-bottom: @size-spacing-x300;
}

.trade-label-empty {
  margin-bottom: 20px;
  display: flex;
  align-items: left;
  color: @text-secondary;
  border-top: 1px solid @divider-primary;
  padding-top: @size-spacing-x350;
}

.trade-button {
  border: none;
  background: none;
  width: 100%;
  cursor: pointer;
  padding: 0;
  outline: none;

  &__unavailable {
    text-align: left;
    margin-bottom: @size-spacing-x600;
  }
}

.sell-all_button {
  border: none;
  background: none;
  cursor: pointer;
}

.trade-data_button {
  display: flex;
}

.trade-move_status {
  margin-right: @size-spacing-x600;
}

.trade-button_footer {
  display: flex;
  align-items: center;
  p {
    margin-left: @size-spacing-x400;
    color: @background-overlay;
  }
}

.trade-display-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.trade-display-right_price {
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.trade-display_right_text {
  color: @text-secondary;
}

.trade-display_graph {
  width: 79px;
  max-height: 52px;
  display: flex;
  align-items: center;
}

.trade-search_button {
  display: flex;
  align-items: center;
}

.trade-search_text {
  margin-left: @size-spacing-x400;
  color: @element-primary;
}

.loader-see_left {
  width: 89px;
  height: @size-spacing-x500;
  border-radius: 10px;
}

.loader-see_right {
  width: @size-spacing-x500;
  height: @size-spacing-x500;
  border-radius: 10px;
}

.sidebar-heading_container {
  padding-bottom: @size-spacing-x900;
  position: relative;

  .sidebar-heading_container__backbutton {
    position: absolute;
    top: 0;
    left: 0;
  }

  p {
    width: 100%;
    text-align: center;
    padding: @size-spacing-x150 @size-spacing-x400;
  }
}

.sidebar-heading {
  padding-bottom: @size-spacing-x300;
}

.loader-title {
  width: 175px;
  height: @size-spacing-x600;
  border-radius: 10px;
}

.trade-buttons_content {
  margin-top: 62px;
}

.trade-content__section {
  padding-bottom: @size-spacing-x800;
}

.trade-unavaliable_balance__content {
  margin-bottom: @size-spacing-x800;
}

.trade-sidebar__doubts {
  color: @text-primary;
}

.trade-button__broker {
  color: @text-primary;
}

.slideInOut-enter,
.slideInOut-leave-to {
  transform: translate(100%, 0);
}

.slideInOut-enter-active,
.slideInOut-enter-leave-active {
  width: 100%;
  height: 100%;
  transition: transform @time @ease;
}
</style>
